/**
 * Typography Tokens
 *
 * Typography system for Bimanova including:
 * - Font families (5 options for user selection)
 * - Type scale (sizes, weights, line heights)
 * - Letter spacing
 * - Font loading utilities
 */

// ============================================================================
// FONT FAMILY OPTIONS
// ============================================================================

export interface FontFamily {
  id: string;
  name: string;
  description: string;
  family: string;      // CSS font-family value
  weights: number[];   // Available weights
  googleFont: string;  // Google Fonts URL
  fallback: string;    // Fallback font stack
}

/**
 * Font family options for user selection
 * Lazy-loaded via Google Fonts
 */
export const FONT_FAMILIES: FontFamily[] = [
  {
    id: 'inter',
    name: 'Inter',
    description: 'Modern and highly readable - Perfect for interfaces',
    family: 'Inter',
    weights: [300, 400, 500, 600, 700],
    googleFont: 'Inter:wght@300;400;500;600;700',
    fallback: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  {
    id: 'ibm-plex-sans',
    name: 'IBM Plex Sans',
    description: 'Professional and elegant - Great for corporate environments',
    family: '"IBM Plex Sans"',
    weights: [300, 400, 500, 600, 700],
    googleFont: 'IBM+Plex+Sans:wght@300;400;500;600;700',
    fallback: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  {
    id: 'nunito',
    name: 'Nunito',
    description: 'Friendly and approachable - Ideal for user-facing applications',
    family: 'Nunito',
    weights: [300, 400, 500, 600, 700],
    googleFont: 'Nunito:wght@300;400;500;600;700',
    fallback: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    id: 'work-sans',
    name: 'Work Sans',
    description: 'Clean and minimalist - Perfect for modern dashboards',
    family: '"Work Sans"',
    weights: [300, 400, 500, 600, 700],
    googleFont: 'Work+Sans:wght@300;400;500;600;700',
    fallback: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    id: 'dm-sans',
    name: 'DM Sans',
    description: 'Elegant and refined - Great for premium applications',
    family: '"DM Sans"',
    weights: [400, 500, 700],
    googleFont: 'DM+Sans:wght@400;500;700',
    fallback: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  {
    // Phase 4.4c-1 re-review fix #1 — display-only serif used by the
    // approved Branding step's "Playfair + Inter" pairing. Pairs with
    // Inter for body. Not intended as a body face.
    id: 'playfair-display',
    name: 'Playfair Display',
    description: 'Editorial serif — pairs with Inter for headings only',
    family: '"Playfair Display"',
    weights: [400, 500, 600, 700],
    googleFont: 'Playfair+Display:wght@400;500;600;700',
    fallback: 'Georgia, serif',
  },
];

export const DEFAULT_FONT_FAMILY = FONT_FAMILIES[0]; // Inter

// ============================================================================
// TYPE SCALE
// ============================================================================

export interface TypeScale {
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
  fontWeight?: number;
}

/**
 * Type scale system (fixed for consistency)
 * Responsive sizing handled via media queries in Tailwind
 */
export const TYPE_SCALE = {
  /** Display text (40px) - Large headings, hero text */
  display: {
    fontSize: '2.5rem',    // 40px
    lineHeight: '1.2',     // 48px
    letterSpacing: '-0.02em',
    fontWeight: 700,
  },

  /** H1 (32px) - Page titles */
  h1: {
    fontSize: '2rem',      // 32px
    lineHeight: '1.25',    // 40px
    letterSpacing: '-0.01em',
    fontWeight: 700,
  },

  /** H2 (24px) - Section headings */
  h2: {
    fontSize: '1.5rem',    // 24px
    lineHeight: '1.33',    // 32px
    letterSpacing: '-0.01em',
    fontWeight: 600,
  },

  /** H3 (20px) - Subsection headings */
  h3: {
    fontSize: '1.25rem',   // 20px
    lineHeight: '1.4',     // 28px
    letterSpacing: '0',
    fontWeight: 600,
  },

  /** H4 (18px) - Card titles */
  h4: {
    fontSize: '1.125rem',  // 18px
    lineHeight: '1.44',    // 26px
    letterSpacing: '0',
    fontWeight: 600,
  },

  /** Body (16px) - Default text */
  body: {
    fontSize: '1rem',      // 16px
    lineHeight: '1.5',     // 24px
    letterSpacing: '0',
    fontWeight: 400,
  },

  /** Body Medium (16px) - Emphasized body text */
  bodyMedium: {
    fontSize: '1rem',      // 16px
    lineHeight: '1.5',     // 24px
    letterSpacing: '0',
    fontWeight: 500,
  },

  /** Small (14px) - Secondary text, labels */
  small: {
    fontSize: '0.875rem',  // 14px
    lineHeight: '1.43',    // 20px
    letterSpacing: '0',
    fontWeight: 400,
  },

  /** Small Medium (14px) - Emphasized small text */
  smallMedium: {
    fontSize: '0.875rem',  // 14px
    lineHeight: '1.43',    // 20px
    letterSpacing: '0',
    fontWeight: 500,
  },

  /** Tiny (12px) - Captions, helper text */
  tiny: {
    fontSize: '0.75rem',   // 12px
    lineHeight: '1.33',    // 16px
    letterSpacing: '0.01em',
    fontWeight: 400,
  },

  /** Tiny Medium (12px) - Emphasized tiny text */
  tinyMedium: {
    fontSize: '0.75rem',   // 12px
    lineHeight: '1.33',    // 16px
    letterSpacing: '0.01em',
    fontWeight: 500,
  },
} as const;

// ============================================================================
// FONT WEIGHTS
// ============================================================================

export const FONT_WEIGHTS = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ============================================================================
// LINE HEIGHTS
// ============================================================================

export const LINE_HEIGHTS = {
  none: '1',
  tight: '1.25',
  snug: '1.375',
  normal: '1.5',
  relaxed: '1.625',
  loose: '2',
} as const;

// ============================================================================
// LETTER SPACING
// ============================================================================

export const LETTER_SPACING = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get font family by ID
 */
export const getFontFamilyById = (id: string): FontFamily | undefined => {
  return FONT_FAMILIES.find((font) => font.id === id);
};

/**
 * Get font family CSS value with fallback
 */
export const getFontFamilyCSS = (id: string): string => {
  const font = getFontFamilyById(id);
  if (!font) return DEFAULT_FONT_FAMILY.fallback;
  return `${font.family}, ${font.fallback}`;
};

/**
 * Generate Google Fonts URL for selected font
 */
export const getGoogleFontsURL = (fontId: string): string => {
  const font = getFontFamilyById(fontId);
  if (!font) return '';
  return `https://fonts.googleapis.com/css2?family=${font.googleFont}&display=swap`;
};

/**
 * Get all font family IDs
 */
export const getFontFamilyIds = (): string[] => {
  return FONT_FAMILIES.map((font) => font.id);
};

/**
 * Get all font family names
 */
export const getFontFamilyNames = (): string[] => {
  return FONT_FAMILIES.map((font) => font.name);
};
